import { useEffect, useRef } from 'react';
import styled from '@emotion/styled';
import * as d3 from 'd3';
import { css, keyframes } from '@emotion/react';
import { tokens } from '@hotjar/hotsauce';
import { getGaugeColorScale } from '@hotjar/webapp/features/SamplingWidget/SessionTrafficCoverage/utils';

const blink = keyframes`
    0% {
      opacity:1;
    }
    50% {
      opacity:0.5;
    }
    100% {
      opacity:1;
    }
`;

const Svg = styled.svg<{ size: number; isCoverageMeasureComplete: boolean }>`
  width: ${({ size }) => size}px;
  height: ${({ size }) => size}px;
  ${({ isCoverageMeasureComplete }) =>
    !isCoverageMeasureComplete &&
    css`
      animation: ${blink} 3s infinite;
    `}
`;

interface Props {
  coveragePercentage: number;
  isCoverageMeasureComplete: boolean;
  isHighlighted: boolean;
}

interface ArcDatum {
  endAngle: number;
}

export const TrafficGaugeStatus = ({
  coveragePercentage,
  isCoverageMeasureComplete,
  isHighlighted,
}: Props): JSX.Element => {
  const gaugeRef = useRef(null);

  const size = 14;
  const arcWidth = 3;
  const outerRadius = size / 2;
  const innerRadius = outerRadius - arcWidth;
  const cornerRadius = (outerRadius - innerRadius) / 2;

  useEffect(() => {
    if (gaugeRef.current) {
      const radScale = d3
        .scaleLinear()
        .domain([0, 100])
        .range([0, 2 * Math.PI]);

      const colorScale = getGaugeColorScale();

      let gaugeColor = tokens.BRAND_COLORS.neutral_700;

      if (isCoverageMeasureComplete) {
        gaugeColor = colorScale(coveragePercentage);
      }

      const arc = d3
        .arc<ArcDatum>()
        .innerRadius(innerRadius)
        .outerRadius(outerRadius)
        .cornerRadius(cornerRadius)
        .startAngle(0);

      d3.select(gaugeRef.current)
        .datum({ endAngle: radScale(coveragePercentage) })
        .attr('d', arc)
        .style('fill', gaugeColor);
    }
  }, [
    gaugeRef,
    coveragePercentage,
    cornerRadius,
    innerRadius,
    isCoverageMeasureComplete,
    outerRadius,
  ]);

  return (
    <Svg size={size} isCoverageMeasureComplete={isCoverageMeasureComplete}>
      <g style={{ transform: 'translate(50%, 50%)' }}>
        <circle
          style={{
            stroke: isHighlighted ? tokens.BRAND_COLORS.neutral_400 : tokens.color.ui_border,
            strokeWidth: 3,
            fill: 'none',
          }}
          r={innerRadius + arcWidth / 2}
        />
        <path ref={gaugeRef} />
      </g>
    </Svg>
  );
};
